import { bunja } from 'bunja';
import { createScopeFromContext, useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { createContext, useMemo, useReducer, useRef, type KeyboardEvent, type Ref } from 'react';

import { LeverageBadge, OrderSideBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import {
  calcFromEntryPercentage,
  calcOrderEstUnrealizedPnl,
} from '@flipster/features/src/shared/calculator/tp-sl-preview';
// ROLLBACK(last-price): trigger price type 셀렉터 재활성화 시 복원.
// import {
//   TriggerPriceTypeSelect,
//   defaultTriggerPriceType,
// } from '@flipster/features/src/shared/trigger-price-type-select';
import { ModalErrorBoundary } from '@flipster/features/src/utils/modal-error-boundary';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Modal } from '@flipster/react/design-system/modal/index';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import Formatted from '@flipster/react/design-utils/formatted';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import Num from '@flipster/utils/num';

import {
  emptyPreset,
  isConfirmDisabled,
  isLegActive,
  isLegValid,
  type AdvancedTpSlPreset,
  type LegType,
  type TpSlLeg,
} from '../advanced-tpsl';
import { symbolBunja, useMarginType } from '../bunja/base';
import { formBunja } from '../bunja/form';
import { realtimeStateBunja } from '../bunja/price';

export const AdvancedTpSlDraftContext = createContext<AdvancedTpSlPreset>(emptyPreset('Long'));
const AdvancedTpSlDraftScope = createScopeFromContext(AdvancedTpSlDraftContext);

const advancedTpSlModalBunja = bunja(() => {
  const initialDraftFromContext = bunja.use(AdvancedTpSlDraftScope);
  return { initialDraftFromContext };
});

/**
 * Advanced TP/SL 설정 모달.
 * 모달은 transient draft(useReducer)를 편집하고, Confirm 시 bunja committed preset으로 반영한다.
 * 진입 시 draft 초기값은 [[AdvancedTpSlDraftContext]]로 주입받는다(직전 inline 입력값 prefill).
 * 탭 전환 시 직전 입력은 초기화된다(최근 탭만 유지).
 * 문자열은 i18n 키로 연결됨. 보류(평문 유지): Limit P&L 안내문(중간에 색상 숫자 포함 rich text).
 */
interface AdvancedTpSlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

type PresetSide = AdvancedTpSlPreset['side'];
const Tabs = createTabsComponent<PresetSide>();

// 가격 입력 Tab 순회 순서. 각 입력은 ref로 등록되며, 미렌더(limit 미사용) 입력은 순회에서 제외된다.
type PriceInputKey = 'tpTrigger' | 'tpLimit' | 'slTrigger' | 'slLimit';
const PRICE_INPUT_ORDER: PriceInputKey[] = ['tpTrigger', 'tpLimit', 'slTrigger', 'slLimit'];

type DraftAction =
  | { kind: 'switchSide'; side: PresetSide }
  | { kind: 'patchLeg'; leg: LegType; patch: Partial<TpSlLeg> };

const draftReducer = (state: AdvancedTpSlPreset, action: DraftAction): AdvancedTpSlPreset => {
  switch (action.kind) {
    case 'switchSide':
      // 탭 전환 시 직전 입력 초기화 (최근 탭만 유지)
      return emptyPreset(action.side);
    case 'patchLeg':
      return { ...state, [action.leg]: { ...state[action.leg], ...action.patch } };
  }
};

const AdvancedTpSlModal = ({ isOpen, onClose, onExit }: AdvancedTpSlModalProps) => (
  <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit} size='small' mode='fullModal'>
    {/* 데이터 레이어(bunja/atom)에서 throw되면 모달을 자동으로 닫는다. */}
    <ModalErrorBoundary onError={onClose}>
      <AdvancedTpSlModalContent onClose={onClose} />
    </ModalErrorBoundary>
  </Modal.Root>
);

const AdvancedTpSlModalContent = ({ onClose }: { onClose: () => void }) => {
  const { symbolInfoAtom } = useBunja(symbolBunja);
  const { calculationPriceAtom, commitAdvancedTpSlAtom, orderStateAtom } = useBunja(formBunja);
  const { amountAtom } = useBunja(realtimeStateBunja);
  // 모달 진입 시 draft 초기값(prefill). opener가 Context로 주입한 값을 scope로 받는다.
  const { initialDraftFromContext } = useBunja(advancedTpSlModalBunja);
  const { pricePrecision } = useAtomValue(symbolInfoAtom);
  // 검증/"from entry" 기준가(anchor): 주문 타입별 entry 가격. (limit→limit price, trigger→trigger price, market→mid)
  const anchor = useAtomValue(calculationPriceAtom);
  const commit = useSetAtom(commitAdvancedTpSlAtom);
  // 예상 P&L 계산용 주문 컨텍스트
  const { leverage } = useAtomValue(orderStateAtom);
  const amount = useAtomValue(amountAtom);

  const [draft, dispatch] = useReducer(draftReducer, initialDraftFromContext);
  const t = useTranslations();

  // 인라인 검증은 trigger price 방향만 본다.
  const confirmDisabled = isConfirmDisabled(draft, { anchor });

  const handleConfirm = () => {
    if (confirmDisabled) return;
    commit(draft);
    onClose();
  };

  // 가격 입력 ref 레지스트리. 각 입력(PriceValueRow)이 callback ref로 자신을 등록한다.
  // DOM 쿼리 대신 이 목록으로 Tab 순회를 제어해, 보조 input 추가 등 DOM 구조 변화에 견고하다.
  const priceInputRefs = useRef<Partial<Record<PriceInputKey, HTMLInputElement | null>>>({});
  // 안정적인 callback ref (렌더마다 새 함수면 ref가 분리/재부착되므로 useMemo로 고정).
  const setPriceInputRef = useMemo(
    () => ({
      tpTrigger: (el: HTMLInputElement | null) => void (priceInputRefs.current.tpTrigger = el),
      tpLimit: (el: HTMLInputElement | null) => void (priceInputRefs.current.tpLimit = el),
      slTrigger: (el: HTMLInputElement | null) => void (priceInputRefs.current.slTrigger = el),
      slLimit: (el: HTMLInputElement | null) => void (priceInputRefs.current.slLimit = el),
    }),
    [],
  );

  // 가격 입력만 Tab으로 순회한다: TP trigger → [TP limit] → SL trigger → [SL limit].
  // 사이의 보조 버튼('from entry')·체크박스(Limit Order/BBO)는 건너뛰고, 경계(첫/마지막)에서는
  // 기본 동작을 유지해 Tab으로 입력 영역을 빠져나갈 수 있게 둔다.
  const handlePriceInputTab = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    // 등록된(렌더된) 입력만 고정 순서대로 모은다 — limit 미사용 시 해당 ref는 null이라 제외된다.
    const orderedInputs = PRICE_INPUT_ORDER.map((key) => priceInputRefs.current[key]).filter(
      (el): el is HTMLInputElement => el != null,
    );
    const index = orderedInputs.indexOf(e.target as HTMLInputElement);
    if (index === -1) return;
    const nextIndex = e.shiftKey ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= orderedInputs.length) return;
    e.preventDefault();
    orderedInputs[nextIndex].focus();
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title label={t('Position.r250401.Take_profit_stop_loss_title')} />
      </Modal.Header>
      <Modal.Content>
        <div className={vstack({ gap: 16, alignItems: 'stretch' })}>
          <Tabs
            type='fixed'
            size='large'
            value={draft.side}
            onChange={(side) => dispatch({ kind: 'switchSide', side })}
          >
            <Tabs.Tab
              value='Long'
              label={t('Autogen_common.r251126.s_Long')}
              selectedColorSet={{ color: 'colors.content.accent.buy', borderColor: 'colors.border.accent.buy' }}
            />
            <Tabs.Tab
              value='Short'
              label={t('Autogen_common.r251126.s_Short')}
              selectedColorSet={{ color: 'colors.content.accent.sell', borderColor: 'colors.border.accent.sell' }}
            />
          </Tabs>

          <SymbolSummary side={draft.side} />

          {/* key={draft.side}: 탭 전환 시 입력 영역을 remount하여 직전 탭의 입력 표시값까지 확실히 초기화한다. */}
          <div key={draft.side} className={vstack({ gap: 16, alignItems: 'stretch' })} onKeyDown={handlePriceInputTab}>
            <TpSlOrderInput
              title={t('Autogen_common.r251126.s_Take_Profit')}
              legType='tp'
              side={draft.side}
              leg={draft.tp}
              precision={pricePrecision}
              anchor={anchor}
              amount={amount}
              leverage={leverage}
              triggerInputRef={setPriceInputRef.tpTrigger}
              limitInputRef={setPriceInputRef.tpLimit}
              onPatch={(patch) => dispatch({ kind: 'patchLeg', leg: 'tp', patch })}
            />
            <TpSlOrderInput
              title={t('Autogen_common.r251126.s_Stop_Loss')}
              legType='sl'
              side={draft.side}
              leg={draft.sl}
              precision={pricePrecision}
              anchor={anchor}
              amount={amount}
              leverage={leverage}
              triggerInputRef={setPriceInputRef.slTrigger}
              limitInputRef={setPriceInputRef.slLimit}
              onPatch={(patch) => dispatch({ kind: 'patchLeg', leg: 'sl', patch })}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          size='large'
          label={t('Common.r250401.Confirm')}
          disabled={confirmDisabled}
          onClick={handleConfirm}
          css={{ w: '100%' }}
        />
      </Modal.Footer>
    </>
  );
};

export default AdvancedTpSlModal;

const SymbolSummary = ({ side }: { side: PresetSide }) => {
  const { symbol, symbolInfoAtom, referencePriceAtom } = useBunja(symbolBunja);
  const { orderStateAtom } = useBunja(formBunja);
  const { marginTypeAtom } = useMarginType();

  const { pricePrecision } = useAtomValue(symbolInfoAtom);
  const referencePrice = useAtomValue(referencePriceAtom);
  const { leverage } = useAtomValue(orderStateAtom);
  const marginType = useAtomValue(marginTypeAtom);

  return (
    <SymbolDisplay
      symbol={symbol}
      icon={<SymbolDisplay.Icon size={32} space={8} />}
      mainLabel={
        <SymbolDisplay.Label
          value='shortName'
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            textStyle: 'Body/XL/Strong',
            color: 'content.default.level-5',
          }}
        >
          <div className={hstack({ gap: 4 })}>
            <OrderSideBadge orderSide={side} hideIcon />
            <LeverageBadge leverage={leverage} marginType={marginType} />
          </div>
        </SymbolDisplay.Label>
      }
      subLabel={
        <Formatted.Number
          value={referencePrice}
          precision={pricePrecision}
          toFixed
          css={{ textStyle: 'Body/M/Regular', color: 'content.default.level-2' }}
        />
      }
    />
  );
};

const TpSlOrderInput = ({
  title,
  legType,
  side,
  leg,
  precision,
  anchor,
  amount,
  leverage,
  triggerInputRef,
  limitInputRef,
  onPatch,
}: {
  title: string;
  legType: LegType;
  side: PresetSide;
  leg: TpSlLeg;
  precision: number;
  anchor: string;
  amount: string;
  leverage: number;
  /** Tab 순회용 ref — trigger/limit 가격 입력을 부모 레지스트리에 등록한다. */
  triggerInputRef?: Ref<HTMLInputElement>;
  limitInputRef?: Ref<HTMLInputElement>;
  onPatch: (patch: Partial<TpSlLeg>) => void;
}) => {
  const t = useTranslations();
  // 방향 에러 문구 기준: market 진입 → "market price", limit/trigger 진입 → "order price". (Don 결정)
  // prop drilling 대신 form 분자에서 직접 읽는다.
  const { priceTypeAtom } = useBunja(formBunja);
  const isMarketEntry = useAtomValue(priceTypeAtom) === 'market';
  // trigger 입력이 있는데 방향이 잘못된 경우만 에러 표기 (execution type과 무관하게 trigger 방향만 검사).
  const directionInvalid =
    isLegActive(leg) && !isLegValid({ ...leg, executionType: 'market' }, { side, legType, anchor });
  const triggerAboveAnchor = (side === 'Long') === (legType === 'tp');

  return (
    <div className={vstack({ gap: 8, alignItems: 'stretch' })}>
      <div className={vstack({ gap: 4, alignItems: 'stretch' })}>
        <div className={flex({ justify: 'space-between', align: 'flex-start', pb: 8 })}>
          <span className={css({ textStyle: 'Body/M/Strong', color: 'content.default.level-5' })}>{title}</span>
          <Checkbox
            checked={leg.executionType === 'limit'}
            onChange={(e) =>
              onPatch({ executionType: e.target.checked ? 'limit' : 'market', isBbo: false, limitPrice: '' })
            }
            css={{ textStyle: 'Body/M/Regular', color: 'content.default.level-5' }}
          >
            {t('Autogen_common.r251126.s_Limit_Order')}
          </Checkbox>
        </div>

        <div className={vstack({ gap: 4, alignItems: 'stretch' })}>
          <div className={css({ bg: 'background.default.level-1', rounded: 12, p: 16 })}>
            <div className={vstack({ gap: 12, alignItems: 'stretch' })}>
              <div className={vstack({ gap: 4, alignItems: 'stretch' })}>
                <div className={flex({ justify: 'space-between', align: 'center' })}>
                  <span className={css({ textStyle: 'Body/M/Regular', color: 'content.default.level-5' })}>
                    {t('Trade.r250401.Trigger_price')}
                  </span>
                  {/* ROLLBACK(last-price): Last/Mark 셀렉터 — MM 준비 완료 시 재활성화.
                  <TriggerPriceTypeSelect
                    value={leg.triggerPriceType ?? defaultTriggerPriceType}
                    onChange={(triggerPriceType) => onPatch({ triggerPriceType })}
                    placement='label-suffix'
                  /> */}
                </div>
                <PriceValueRow
                  value={leg.triggerPrice}
                  onChange={(triggerPrice) => onPatch({ triggerPrice })}
                  precision={precision}
                  anchor={anchor}
                  inputRef={triggerInputRef}
                />
              </div>
              {/* 방향 오류 메시지는 Trigger Price 박스 안(값 아래)에 표기한다 (Figma 12116:34334) */}
              {/* 인라인 에러는 trigger price 방향에만 적용. 기준가 문구는 진입 타입별:
                  market 진입 → market price, limit/trigger 진입 → order price.
                  https://prextech.slack.com/archives/C0AQX0CL77F/p1781831737840119?thread_ts=1781246831.641929&cid=C0AQX0CL77F
                  청산가 인라인 에러는 Advanced 페이지에서 노출하지 않는다. */}
              {directionInvalid && (
                <p className={css({ textStyle: 'Body/M/Regular', color: 'content.state.negative' })}>
                  {t(
                    triggerAboveAnchor
                      ? isMarketEntry
                        ? 'Trade.r250401.Price_above_market_required_error'
                        : 'Trade.r250401.Price_above_order_required_error'
                      : isMarketEntry
                        ? 'Trade.r250401.Price_below_market_required_error'
                        : 'Trade.r250401.Price_below_order_required_error',
                  )}
                </p>
              )}
            </div>
          </div>

          {leg.executionType === 'limit' && (
            <div className={css({ bg: 'background.default.level-1', rounded: 12, px: 16, py: 12 })}>
              <div className={vstack({ gap: 4, alignItems: 'stretch' })}>
                <div className={flex({ gap: 8, align: 'center' })}>
                  <span className={css({ flex: 1, textStyle: 'Body/XS/Regular', color: 'content.default.level-5' })}>
                    {t('Trade.r260420.Limit_price')}
                  </span>
                  <Checkbox
                    checked={leg.isBbo}
                    onChange={(e) => onPatch({ isBbo: e.target.checked, limitPrice: '' })}
                    css={{ textStyle: 'Body/M/Regular', color: 'content.default.level-5' }}
                  >
                    {t('Autogen_common.r251126.s_BBO')}
                  </Checkbox>
                </div>
                {leg.isBbo ? (
                  // BBO 체크 시: 체결 가격은 발동 시점의 최우선 호가로 자동 지정 → 입력 대신 안내 문구
                  <span className={css({ textStyle: 'Title/S/Monospace', color: 'content.default.level-4' })}>
                    {t('Trade.r251104.Best_counterparty')}
                  </span>
                ) : (
                  <PriceValueRow
                    value={leg.limitPrice}
                    onChange={(limitPrice) => onPatch({ limitPrice })}
                    precision={precision}
                    anchor={anchor}
                    inputRef={limitInputRef}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {leg.executionType === 'limit' && leg.isBbo && (
        <p className={css({ textStyle: 'Body/S/Regular', color: 'content.default.level-1' })}>
          {t('Trade.r260420.Limit_tp_sl_bbo_desc')}
        </p>
      )}

      {leg.executionType === 'limit' && !leg.isBbo && (
        <OrderPnlHint
          mode='limit'
          side={side}
          entryPrice={anchor}
          exitPrice={leg.limitPrice}
          amount={amount}
          leverage={leverage}
        />
      )}

      {/* Market(트리거만) 주문도 trigger price가 입력되면 그 가격 기준 예상 P&L을 보여준다. */}
      {leg.executionType === 'market' && isLegActive(leg) && (
        <OrderPnlHint
          mode='market'
          side={side}
          entryPrice={anchor}
          exitPrice={leg.triggerPrice}
          amount={amount}
          leverage={leverage}
        />
      )}
    </div>
  );
};

const PNL_HINT_MESSAGE_ID = {
  // Limit: limit price에 체결되므로 그 가격 기준 P&L. (미입력 시 '--')
  limit:
    'Trade.r260611.Placed_as_a_limit_order_may_not_execute_if_the_market_doesn_t_reach_your_price_estimated_pnl_is_pnl_usdt_roi_roi_percentage',
  // Market: 발동 시 trigger price 부근에서 체결되므로 trigger price 기준 P&L.
  market: 'Trade.r260611.Placed_as_a_market_order_when_triggered_estimated_pnl_will_be_pnl_usdt_roi_roi_percentage',
} as const;

/**
 * TP/SL leg의 예상 P&L 안내. Limit/Market 모두 UI는 동일하고, 기준가(exitPrice)와 안내 문구만 다르다.
 * - mode='limit' → exitPrice = limit price (미입력이면 산출 불가 → '--')
 * - mode='market' → exitPrice = trigger price
 * USDT·% 는 메시지에 리터럴로 포함되므로 pnl/roi 자리에는 색상 숫자 노드(또는 '--')만 넣는다.
 */
const OrderPnlHint = ({
  mode,
  side,
  entryPrice,
  exitPrice,
  amount,
  leverage,
}: {
  mode: 'limit' | 'market';
  side: PresetSide;
  entryPrice: string;
  exitPrice: string;
  amount: string;
  leverage: number;
}) => {
  const hasPrice = !!exitPrice && !Num(exitPrice).isZero();
  const { estUnrealizedPnl, estUnrealizedPnlPct } = calcOrderEstUnrealizedPnl(
    side,
    entryPrice,
    exitPrice,
    amount,
    leverage,
  );

  return (
    <p className={css({ textStyle: 'Body/S/Regular', color: 'content.default.level-1' })}>
      <FormattedMessage
        id={PNL_HINT_MESSAGE_ID[mode]}
        values={{
          // key: t.rich 가 placeholder 값을 배열 자식으로 합치므로 element 값에 key 부여(React key 경고 방지).
          pnl: hasPrice ? (
            <Formatted.Number key='pnl' value={estUnrealizedPnl} precision={2} toFixed withColor withPlus />
          ) : (
            '--'
          ),
          roi: hasPrice ? (
            <Formatted.Number key='roi' value={estUnrealizedPnlPct} precision={2} toFixed withColor withPlus />
          ) : (
            '--'
          ),
        }}
      />
    </p>
  );
};

const PriceValueRow = ({
  value,
  onChange,
  precision,
  anchor,
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  precision: number;
  anchor: string;
  inputRef?: Ref<HTMLInputElement>;
}) => {
  const t = useTranslations();
  const fromEntryPercentage = calcFromEntryPercentage(value, anchor);

  return (
    <div className={flex({ gap: 8, align: 'flex-end' })}>
      <FormattedNumberInput
        ref={inputRef}
        value={value}
        onChange={onChange}
        precision={precision}
        placeholder={t('Autogen_common.r251126.s_Enter_price')}
        className={css({
          flex: 1,
          minW: 0,
          bg: 'transparent',
          border: 'none',
          outline: 'none',
          p: 0,
          textStyle: 'Title/S/Monospace',
          color: 'content.default.level-5',
          _placeholder: { color: 'content.default.level-1' },
        })}
      />
      <div className={hstack({ gap: 4 })}>
        <Formatted.Percent
          withColor
          withPlus
          toFixed
          precision={2}
          method='round-half-up'
          value={fromEntryPercentage}
          css={{ textStyle: 'Text/M/Monospace/Regular' }}
        />
        <button
          type='button'
          // TP/SL 모달과 동일: 클릭 시 가격을 entry price(anchor)로 채운다 (= 0% from entry)
          onClick={() => onChange(anchor)}
          className={css({
            textStyle: 'Body/M/Monospace/Strong',
            color: 'content.default.level-5',
            borderBottomWidth: '0.5px',
            borderBottomStyle: 'solid',
            borderColor: 'content.default.level-2',
            cursor: 'pointer',
          })}
        >
          {t('Trade.r250401.From_entry')}
        </button>
      </div>
    </div>
  );
};
